import { useCallback, useEffect, useState } from "react";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import { Alert, Box, Stack, Typography } from "@mui/material";
import {
  DialogCustomButton,
  DialogCustomContent,
  DialogCustomTitle,
  StyledTextarea,
} from "./styles";
import { AUTH_COMPLIANCE_EMAIL } from "../../../company/models";
import { VerificationStatus } from "../../utils/getStatus";
import { useVerificationStatusChange } from "../../hooks/useVerificationStatusChange";
import { getWidgetLink } from "../../network";

const getTextMessage = (data: any, widgetLink: string) => {
  const complianceEmail =
    localStorage.getItem(AUTH_COMPLIANCE_EMAIL) || "compliance@id.vnx.li";

  return `Dear ${data?.additionalItems?.userInformationParsed?.first_name},

Thank you for registering!

It appears that your KYC process is not finalized.

Please follow the link below to change your data: ${widgetLink}

Please don't hesitate to contact us on ${complianceEmail} - we will be happy to assist!

Best regards,
VNX ID team`;
};

export type Props = {
  onClose: (
    reason: "cancel" | VerificationStatus.INFORMATION_REQUESTED
  ) => void;
  open: boolean;
  verificationId: string;
  data: any;
  value: string;
};

export const DocumentRequestInfoDialog = (props: Props) => {
  const { verificationId, onClose, open, data, value } = props;
  const [message, setMessage] = useState("");
  const [isWidgetLinkLoading, setIsWidgetLinkLoading] = useState(false);
  const [isWidgetLinkLoaded, setIsWidgetLinkLoaded] = useState(false);
  const [widgetLinkError, setWidgetLinkError] = useState<Error | null>(null);
  const [
    changeStatusResult,
    changeStatusLoading,
    changeStatusError,
    changeStatusRequest,
  ] = useVerificationStatusChange(verificationId);

  useEffect(() => {
    if (changeStatusResult) {
      onClose(VerificationStatus.INFORMATION_REQUESTED);
    }
  }, [onClose, changeStatusResult]);

  const handleCancel = useCallback(() => {
    onClose("cancel");
  }, [onClose]);

  const handleOk = useCallback(() => {
    const messageReplaced = message.replace(/\n/g, "<br />");
    changeStatusRequest(value, { message: messageReplaced });
  }, [changeStatusRequest, value, message]);

  useEffect(() => {
    if (!open) {
      setMessage("");
      setWidgetLinkError(null);
      setIsWidgetLinkLoaded(false);
      setIsWidgetLinkLoading(false);
      return;
    }

    let isActive = true;

    const loadWidgetLink = async () => {
      setMessage("");
      setWidgetLinkError(null);
      setIsWidgetLinkLoaded(false);
      setIsWidgetLinkLoading(true);

      try {
        const { link } = await getWidgetLink(
          verificationId,
          "user_notification"
        );

        if (!link) {
          throw new Error("Widget link is unavailable");
        }

        if (isActive) {
          setMessage(getTextMessage(data, link));
          setIsWidgetLinkLoaded(true);
        }
      } catch (error) {
        if (isActive) {
          setWidgetLinkError(
            error instanceof Error
              ? error
              : new Error("Failed to load widget link")
          );
        }
      } finally {
        if (isActive) {
          setIsWidgetLinkLoading(false);
        }
      }
    };

    loadWidgetLink();

    return () => {
      isActive = false;
    };
  }, [data, open, verificationId]);

  return (
    <Dialog
      sx={{
        "& .MuiDialog-paper": {
          width: "450px",
          borderRadius: "16px",
        },
      }}
      maxWidth='xs'
      open={open}
      id='document-review-dialog'
      keepMounted
    >
      <DialogCustomTitle>Message to client</DialogCustomTitle>
      <DialogCustomContent>
        Please specify here what the client needs to correct or add
        <StyledTextarea
          value={message}
          onChange={(e: any) => setMessage(e.target.value)}
          aria-label='minimum height'
          minRows={3}
          placeholder='Type something'
          disabled={isWidgetLinkLoading}
        ></StyledTextarea>
        <Typography sx={{ fontSize: "0.8em", textAlign: "justify", mt: 2 }}>
          If Selfie or Passport sections are not filled or missing - please
          instruct the client:
          <br />
          Identification section (i.e. the ID and selfie verification) has not
          been completed. If the computer camera does not capture a sufficiently
          clear image, a possible solution is to finalize the KYC on your phone.
        </Typography>
        {isWidgetLinkLoading && (
          <Alert severity='info' sx={{ mt: 2 }}>
            Loading widget link...
          </Alert>
        )}
        {widgetLinkError !== null && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {widgetLinkError.message}
          </Alert>
        )}
        {changeStatusError !== null && (
          <Alert severity='error'>{changeStatusError.toString()}</Alert>
        )}
      </DialogCustomContent>
      <DialogActions sx={{ padding: "0 32px 32px" }}>
        <Stack
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
          width='100%'
        >
          <Box>
            <DialogCustomButton
              autoFocus
              onClick={handleCancel}
              disabled={changeStatusLoading}
              variant='outlined'
            >
              Cancel
            </DialogCustomButton>
          </Box>
          <Box>
            <DialogCustomButton
              onClick={handleOk}
              disabled={
                changeStatusLoading ||
                isWidgetLinkLoading ||
                !isWidgetLinkLoaded ||
                widgetLinkError !== null
              }
              variant='contained'
              color='primary'
            >
              Send
            </DialogCustomButton>
          </Box>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
